import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { has as _has } from 'lodash';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';

import { SearchContext, SearchedValue } from '../../+state/service-locator/service-locator.models';
import { STATE_CODES } from '../../models/forms.models';
import { ValidationService } from '../../services/validation.service';

@Component({
  selector: 'drp-search-by-name-form',
  templateUrl: './search-by-name-form.component.html',
  styleUrls: ['./search-by-name-form.component.scss'],
  standalone: false
})
export class SearchByNameFormComponent implements OnInit {
  /** The current value of the search by name form to emit on submit */
  @Output() searchedName = new EventEmitter<SearchedValue>();

  formGroup: UntypedFormGroup;
  submitted = false;
  states: Array<string>;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private validationService: ValidationService
  ) {}

  ngOnInit() {
    this.states = STATE_CODES;
    this.setupForm();
  }

  submitSearchByName() {
    this.submitted = true;
    if (this.formGroup.valid) {
      this.searchedName.emit(this.formGroup.value);
      this.resetForm(this.formGroup.value);
    }
  }

  private validateZipCodeExists(control: AbstractControl) {
    return this.validationService.zipExists(control.value, SearchContext.DRP_SHOP_SEARCH).pipe(
      map(res => {
        return res.exactAddressMatchIndicator ? null : { nonExistentZipcode: true };
      })
    );
  }

  private setupForm() {
    this.formGroup = this.formBuilder.group({
      shopName: ['', [ValidationService.shopNameGenericValidation]],
      city: ['', [Validators.required, ValidationService.shopNameCityValidation]],
      state: ['', [Validators.required]],
      zip: [
        '',
        [Validators.required, ValidationService.zipCodeNumbersAndLength],
        [this.validateZipCodeExists.bind(this)]
      ]
    });

    this.formGroup.valueChanges.pipe(distinctUntilChanged()).subscribe(values => {
      // As the user types, update which fields are required based on the data entered
      if (
        this.zipShopNameValid() &&
        !!this.formGroup.get('city').validator &&
        !!this.formGroup.get('state').validator
      ) {
        this.formGroup.get('city').clearValidators();
        this.formGroup.get('city').updateValueAndValidity();
        this.formGroup.get('state').clearValidators();
        this.formGroup.get('state').updateValueAndValidity();
      } else if (this.stateCityValid() && !!this.formGroup.get('zip').validator) {
        this.formGroup.get('zip').clearValidators();
        this.formGroup.get('zip').clearAsyncValidators();
        this.formGroup.get('zip').updateValueAndValidity();
      }
    });
  }

  private resetForm(formValue) {
    this.submitted = false;
    this.formGroup.reset();
    this.formGroup.setValue(formValue);
    this.resetValidation();
  }

  private resetValidation() {
    this.formGroup
      .get('city')
      .setValidators([Validators.required, ValidationService.shopNameCityValidation]);
    this.formGroup.get('city').updateValueAndValidity();
    this.formGroup.get('state').setValidators([Validators.required]);
    this.formGroup.get('state').updateValueAndValidity();
    this.formGroup
      .get('zip')
      .setValidators([Validators.required, ValidationService.zipCodeNumbersAndLength]);
    this.formGroup.get('zip').setAsyncValidators([this.validateZipCodeExists.bind(this)]);
    this.formGroup.get('zip').updateValueAndValidity();
  }

  private stateCityValid(): boolean {
    return (
      this.formGroup.get('shopName').valid &&
      this.formGroup.get('city').valid &&
      this.formGroup.get('state').valid &&
      (this.formGroup.get('zip').value === '' || this.formGroup.get('zip').value === null)
    );
  }

  private zipShopNameValid(): boolean {
    return (
      this.formGroup.get('shopName').valid &&
      this.formGroup.get('zip').valid &&
      (this.formGroup.get('city').value === '' || this.formGroup.get('city').value === null) &&
      (this.formGroup.get('state').value === '' || this.formGroup.get('state').value === null)
    );
  }
}
